//@flow
import { getUserInfo } from "../UserInfo";

export type AnalyticsIdentity = {|
  user_id?: string,
  account_id: string,
  account_type: "individual" | "organization",
  owner_publisher_id?: number,
  actor_is_owner: boolean,
  email?: string,
  effective_plan?: string,
  is_paid: boolean,
  account_created_before_tracking?: boolean,
  signup_context?: string
|};

type BrowserEventName =
  | "form_create_started"
  | "share_action_completed"
  | "paywall_viewed"
  | "upgrade_clicked"
  | "cancellation_started"
  | "account_daily_builder_activity";

const ALLOWED_EVENTS: Array<BrowserEventName> = [
  "form_create_started",
  "share_action_completed",
  "paywall_viewed",
  "upgrade_clicked",
  "cancellation_started",
  "account_daily_builder_activity"
];

const ALLOWED_EXTRA_PROPERTIES = [
  "form_id",
  "form_type",
  "template_or_manual",
  "feature_name",
  "activity_date"
];
const STORAGE_PREFIX = "wispform:amplitude:v1";

// Session Replay records the dashboard for accounts created from this date, for
// their first 14 days, to stay inside Amplitude's free monthly replay quota.
// Only typed input is masked (the plugin default).
const SESSION_REPLAY_START = Date.parse("2026-10-17T00:00:00-07:00");
const SESSION_REPLAY_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;
const SESSION_REPLAY_PLUGIN_NAME = "@amplitude/plugin-session-replay-browser";

let analyticsIdentity: ?AnalyticsIdentity = null;
let amplitudeModulePromise: ?Promise<any> = null;
let sdkInitialized = false;
let initializedAccountId: ?string = null;
let identityVersion = 0;
let pendingBuilderActivity = false;
let sessionReplayActive = false;
const inMemoryCaps = new Set();

function isEnabled(): boolean {
  return (
    process.env.REACT_APP_AMPLITUDE_ANALYTICS_ENABLED === "true" &&
    Boolean(process.env.REACT_APP_AMPLITUDE_API_KEY)
  );
}

function isValidIdentity(identity: any): boolean {
  return Boolean(
    identity &&
      typeof identity.account_id === "string" &&
      identity.account_id.indexOf("account:") === 0 &&
      (identity.account_type === "individual" ||
        identity.account_type === "organization") &&
      typeof identity.actor_is_owner === "boolean" &&
      typeof identity.is_paid === "boolean"
  );
}

function loadAmplitude(): Promise<any> {
  if (!amplitudeModulePromise) {
    amplitudeModulePromise = import(
      /* webpackChunkName: "amplitude-analytics" */ "@amplitude/analytics-browser"
    ).catch(() => null);
  }
  return amplitudeModulePromise;
}

function safeReset(module: any) {
  try {
    module && module.reset && module.reset();
  } catch (error) {}
}

export function shouldRecordSessionReplay(
  accountCreatedAt: ?string,
  now: number = Date.now()
): boolean {
  const createdAt = Date.parse(String(accountCreatedAt || ""));
  if (isNaN(createdAt)) return false;
  return (
    createdAt >= SESSION_REPLAY_START &&
    now - createdAt < SESSION_REPLAY_WINDOW_MS
  );
}

function stopSessionReplay(module: any) {
  if (!sessionReplayActive) return;
  sessionReplayActive = false;
  try {
    module.remove(SESSION_REPLAY_PLUGIN_NAME);
  } catch (error) {}
}

function syncSessionReplay(module: any) {
  let accountCreatedAt = null;
  try {
    const userInfo = getUserInfo();
    accountCreatedAt = userInfo ? userInfo.created_at : null;
  } catch (error) {}

  if (!shouldRecordSessionReplay(accountCreatedAt)) {
    stopSessionReplay(module);
    return;
  }
  if (sessionReplayActive) return;

  sessionReplayActive = true;
  import(
    /* webpackChunkName: "amplitude-session-replay" */ "@amplitude/plugin-session-replay-browser"
  )
    .then(replay => {
      // The user may have signed out or switched account while this loaded.
      if (!sessionReplayActive) return;
      module.add(
        replay.sessionReplayPlugin({
          sampleRate: 1,
          // A replay only plays back if its session contains an event.
          forceSessionTracking: true
        })
      );
    })
    .catch(() => {
      sessionReplayActive = false;
    });
}

function removePersistedIdentity() {
  const apiKey = process.env.REACT_APP_AMPLITUDE_API_KEY;
  if (!apiKey) return;

  try {
    window.localStorage.removeItem(`AMP_${apiKey.slice(0, 10)}`);
  } catch (error) {}
}

function syncCurrentAccountProperties(module: any, identity: AnalyticsIdentity) {
  try {
    const identify = new module.Identify();
    identify.set(
      "current_plan",
      String(identity.effective_plan || "basic").toLowerCase()
    );
    identify.set("current_is_paid", identity.is_paid);
    identify.set("account_type", identity.account_type);
    if (identity.email) {
      identify.set("email", identity.email);
    }
    module.identify(identify);
  } catch (error) {}
}

export function initializeAmplitudeAnalytics(identity: ?AnalyticsIdentity) {
  if (!isEnabled() || !identity || !isValidIdentity(identity)) {
    resetAmplitudeAnalytics();
    return;
  }

  const resolvedIdentity: AnalyticsIdentity = identity;
  analyticsIdentity = resolvedIdentity;
  identityVersion += 1;
  const versionAtInitialization = identityVersion;
  const accountId = resolvedIdentity.account_id;

  loadAmplitude().then(module => {
    if (
      !module ||
      versionAtInitialization !== identityVersion ||
      !analyticsIdentity ||
      analyticsIdentity.account_id !== accountId
    ) {
      return;
    }

    if (initializedAccountId && initializedAccountId !== accountId) {
      safeReset(module);
      initializedAccountId = null;
    }

    if (initializedAccountId === accountId) {
      syncCurrentAccountProperties(module, resolvedIdentity);
      syncSessionReplay(module);
      return;
    }

    try {
      if (sdkInitialized) {
        module.setUserId(accountId);
        initializedAccountId = accountId;
        syncCurrentAccountProperties(module, resolvedIdentity);
        syncSessionReplay(module);
        return;
      }

      module.init(String(process.env.REACT_APP_AMPLITUDE_API_KEY), accountId, {
        defaultTracking: false,
        autocapture: false,
        identityStorage: "localStorage",
        trackingOptions: {
          ipAddress: false
        }
      });
      sdkInitialized = true;
      initializedAccountId = accountId;
      syncCurrentAccountProperties(module, resolvedIdentity);
      syncSessionReplay(module);
    } catch (error) {}
  });

  if (pendingBuilderActivity) {
    pendingBuilderActivity = false;
    trackDailyBuilderActivity();
  }
}

export function resetAmplitudeAnalytics() {
  analyticsIdentity = null;
  pendingBuilderActivity = false;
  identityVersion += 1;
  initializedAccountId = null;
  removePersistedIdentity();

  if (amplitudeModulePromise) {
    amplitudeModulePromise.then(module => {
      if (module) stopSessionReplay(module);
      safeReset(module);
    });
  }
}

function normalizedFeatureName(featureName: ?string): string {
  const normalized = String(featureName || "unknown")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80);
  return normalized || "unknown";
}

function eventProperties(properties: Object): Object {
  if (!analyticsIdentity) return {};

  const allowedProperties = ALLOWED_EXTRA_PROPERTIES.reduce(
    (result, propertyName) => {
      const value = properties[propertyName];
      if (value !== null && typeof value !== "undefined") {
        result[propertyName] = value;
      }
      return result;
    },
    {}
  );

  return {
    schema_version: 1,
    environment:
      process.env.REACT_APP_DEPLOYMENT_ENV || process.env.NODE_ENV || "unknown",
    source: "react",
    account_id: analyticsIdentity.account_id,
    account_type: analyticsIdentity.account_type,
    effective_plan: analyticsIdentity.effective_plan,
    is_paid: analyticsIdentity.is_paid,
    account_created_before_tracking:
      analyticsIdentity.account_created_before_tracking,
    signup_context: analyticsIdentity.signup_context,
    actor_is_owner: analyticsIdentity.actor_is_owner,
    ...allowedProperties
  };
}

function track(
  eventName: BrowserEventName,
  properties: Object = {},
  ownerOnly: boolean = false
): boolean {
  if (
    !isEnabled() ||
    !analyticsIdentity ||
    !ALLOWED_EVENTS.includes(eventName) ||
    (ownerOnly && !analyticsIdentity.actor_is_owner)
  ) {
    return false;
  }

  const accountId = analyticsIdentity.account_id;
  const versionAtTrack = identityVersion;
  const propertiesAtTrack = eventProperties(properties);

  loadAmplitude().then(module => {
    if (
      !module ||
      !initializedAccountId ||
      initializedAccountId !== accountId ||
      versionAtTrack !== identityVersion
    ) {
      return;
    }

    try {
      module.track(eventName, propertiesAtTrack);
    } catch (error) {}
  });
  return true;
}

function wasAlreadyTracked(storageKey: string): boolean {
  if (inMemoryCaps.has(storageKey)) return true;

  try {
    return window.localStorage.getItem(storageKey) === "1";
  } catch (error) {
    return false;
  }
}

function markTracked(storageKey: string) {
  inMemoryCaps.add(storageKey);
  try {
    window.localStorage.setItem(storageKey, "1");
  } catch (error) {}
}

export function trackFormCreateStarted(
  formType: ?string,
  templateOrManual: "template" | "manual"
) {
  track("form_create_started", {
    form_type: formType,
    template_or_manual: templateOrManual
  });
}

export function trackShareActionCompleted(formId: ?number) {
  if (!analyticsIdentity || !formId) return;

  const storageKey = `${STORAGE_PREFIX}:share:${analyticsIdentity.account_id}:${String(
    formId
  )}`;
  if (wasAlreadyTracked(storageKey)) return;

  if (track("share_action_completed", { form_id: formId })) {
    markTracked(storageKey);
  }
}

export function trackPaywallViewed(featureName: ?string) {
  if (!analyticsIdentity || !analyticsIdentity.actor_is_owner) return;

  const feature = normalizedFeatureName(featureName);
  const utcDate = new Date().toISOString().slice(0, 10);
  const storageKey = `${STORAGE_PREFIX}:paywall:${analyticsIdentity.account_id}:${feature}:${utcDate}`;
  if (wasAlreadyTracked(storageKey)) return;

  if (track("paywall_viewed", { feature_name: feature }, true)) {
    markTracked(storageKey);
  }
}

export function trackUpgradeClicked(featureName?: string) {
  track(
    "upgrade_clicked",
    { feature_name: normalizedFeatureName(featureName) },
    true
  );
}

export function trackCancellationStarted() {
  track("cancellation_started", {}, true);
}

export function trackDailyBuilderActivity() {
  if (process.env.REACT_APP_AMPLITUDE_BUILDER_ACTIVITY_ENABLED !== "true") {
    return;
  }

  if (!analyticsIdentity) {
    pendingBuilderActivity = true;
    return;
  }

  const activityDate = new Date().toISOString().slice(0, 10);
  const storageKey = `${STORAGE_PREFIX}:builder:${analyticsIdentity.account_id}:${activityDate}`;
  if (wasAlreadyTracked(storageKey)) return;

  if (track("account_daily_builder_activity", { activity_date: activityDate })) {
    markTracked(storageKey);
  }
}
